import type { ChatMessageQueryOptions } from '#src/components/message-list';
import type { GetMessagesQueryVariables } from '#src/graphql/generated';

import { NetworkStatus } from '@apollo/client';
import { useApolloClient, useMutation, useQuery } from '@apollo/client/react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { toaster } from '#src/components/ui/toaster';
import { CHAT_MESSAGE_QUERY_PROCESSOR_ID } from '#src/constants/processors';
import { mergeCreateMessageIntoGetMessagesCache } from '#src/graphql/cache/merge-create-message-into-get-messages';
import { CREATE_MESSAGE } from '#src/graphql/mutations/create-message';
import { EXECUTE_PROCESSOR } from '#src/graphql/mutations/execute-processor';
import { GET_MESSAGES } from '#src/graphql/queries/messages';
import { useThreadFocusSearchParams } from '#src/hooks/use-thread-focus-search-params';
import { useCurrentUser } from '#src/providers/user-provider';
import {
  findThreadRootForMessage,
  resolveFocusParamToRootId,
} from '#src/utils/message-thread-focus';
import { persistProcessorFailureAsThreadReply } from '#src/utils/persist-processor-failure-message';

export interface UseDocumentScopedMessagesOptions {
  /** When false, skips the messages query (e.g. drawer closed). */
  enabled: boolean;
}

export function useDocumentScopedMessages(
  documentId: string | undefined,
  { enabled }: UseDocumentScopedMessagesOptions,
) {
  const user = useCurrentUser();
  const apolloClient = useApolloClient();
  const [messageText, setMessageText] = useState('');
  const {
    rawFocusMessageId,
    expandThread,
    collapseThread,
    replaceStripFocusParam,
  } = useThreadFocusSearchParams();

  const queryEnabled =
    Boolean(documentId) && enabled && Boolean(user.email?.trim());

  const getMessagesVariables = useMemo<GetMessagesQueryVariables>(
    () => ({
      input: {
        contextFilters: [{ name: 'documentId', value: documentId ?? '' }],
      },
    }),
    [documentId],
  );

  const [createMessage, { loading: isSendingMessage }] =
    useMutation(CREATE_MESSAGE);
  const [executeChatMessageQuery] = useMutation(EXECUTE_PROCESSOR);

  const [chatMessageQueryLoadingId, setChatMessageQueryLoadingId] = useState<
    string | null
  >(null);

  const {
    data: messagesData,
    error: messagesError,
    networkStatus: messagesNetworkStatus,
    refetch: refetchMessages,
  } = useQuery(GET_MESSAGES, {
    variables: getMessagesVariables,
    fetchPolicy: 'cache-and-network',
    skip: !queryEnabled,
  });

  const isMessagesInitialLoad =
    messagesNetworkStatus === NetworkStatus.loading && messagesData == null;

  const sortedMessages = useMemo(
    () =>
      [...(messagesData?.messages || [])].sort(
        (a, b) =>
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      ),
    [messagesData?.messages],
  );

  const focusedRootId = useMemo(
    () => resolveFocusParamToRootId(rawFocusMessageId, sortedMessages),
    [rawFocusMessageId, sortedMessages],
  );

  const displayMessages = useMemo(
    () =>
      focusedRootId
        ? sortedMessages.filter((m) => m.id === focusedRootId)
        : sortedMessages,
    [sortedMessages, focusedRootId],
  );

  useEffect(() => {
    if (!queryEnabled || isMessagesInitialLoad) return;
    if (rawFocusMessageId && !focusedRootId) {
      replaceStripFocusParam();
    }
  }, [
    queryEnabled,
    isMessagesInitialLoad,
    rawFocusMessageId,
    focusedRootId,
    replaceStripFocusParam,
  ]);

  const handleRunChatMessageQuery = useCallback(
    async (
      messageId: string,
      query: string,
      options?: ChatMessageQueryOptions,
    ) => {
      setChatMessageQueryLoadingId(messageId);
      try {
        const variables: Record<string, unknown> = { messageId, query };
        if (options?.iterative) {
          variables.iterative = true;
        }
        const { data } = await executeChatMessageQuery({
          variables: {
            input: {
              processorId: CHAT_MESSAGE_QUERY_PROCESSOR_ID,
              variables,
            },
          },
        });
        const ep = data?.executeProcessor;
        const inner = ep?.result as
          | {
              success?: boolean;
              error?: string;
              documentUpdated?: boolean;
            }
          | undefined;
        const instanceId = ep?.instanceId?.trim();
        const root = findThreadRootForMessage(sortedMessages, messageId);

        if (!ep?.success) {
          if (root) {
            const persist = await persistProcessorFailureAsThreadReply(
              apolloClient,
              root.id,
              ep?.error ?? 'Processor failed',
              instanceId,
            );
            if (!persist.ok) {
              toaster.create({
                title: 'Could not save error to thread',
                description: persist.error,
                type: 'warning',
              });
            }
            await refetchMessages();
          } else {
            toaster.create({
              title: 'Could not run AI action',
              description: ep?.error ?? 'Processor failed',
              type: 'error',
            });
          }
          return;
        }
        if (inner?.success === false) {
          if (root) {
            const persist = await persistProcessorFailureAsThreadReply(
              apolloClient,
              root.id,
              inner.error ?? 'Request failed',
              instanceId,
            );
            if (!persist.ok) {
              toaster.create({
                title: 'Could not save error to thread',
                description: persist.error,
                type: 'warning',
              });
            }
            await refetchMessages();
          } else {
            toaster.create({
              title: 'Could not run AI action',
              description: inner.error ?? 'Request failed',
              type: 'error',
            });
          }
          return;
        }
        await refetchMessages();
      } catch (error) {
        const root = findThreadRootForMessage(sortedMessages, messageId);
        const description =
          error instanceof Error
            ? error.message
            : 'An unexpected error occurred';
        if (root) {
          const persist = await persistProcessorFailureAsThreadReply(
            apolloClient,
            root.id,
            description,
            undefined,
          );
          if (!persist.ok) {
            toaster.create({
              title: 'Could not save error to thread',
              description: persist.error,
              type: 'warning',
            });
          }
          await refetchMessages();
        } else {
          toaster.create({
            title: 'Could not run AI action',
            description,
            type: 'error',
          });
        }
      } finally {
        setChatMessageQueryLoadingId(null);
      }
    },
    [apolloClient, executeChatMessageQuery, refetchMessages, sortedMessages],
  );

  const handleSendMessage = useCallback(async () => {
    const value = messageText.trim();
    if (!value || !documentId) return;
    const hadNoThreadFocus = !focusedRootId;
    try {
      const { data } = await createMessage({
        variables: {
          input: {
            value,
            parentMessageId: focusedRootId ?? undefined,
            contexts: [{ name: 'documentId', value: documentId }],
          },
        },
        update(cache, { data: mutationData }) {
          if (mutationData?.createMessage) {
            mergeCreateMessageIntoGetMessagesCache(
              cache,
              mutationData.createMessage,
              getMessagesVariables,
            );
          }
        },
      });
      setMessageText('');
      if (hadNoThreadFocus && data?.createMessage?.id) {
        expandThread(data.createMessage.id);
      }
    } catch (error) {
      toaster.create({
        title: 'Failed to send message',
        description:
          error instanceof Error
            ? error.message
            : 'An unexpected error occurred',
        type: 'error',
      });
    }
  }, [
    messageText,
    documentId,
    focusedRootId,
    createMessage,
    getMessagesVariables,
    expandThread,
  ]);

  const clearMessageDraft = useCallback(() => {
    setMessageText('');
  }, []);

  return {
    queryEnabled,
    messageText,
    setMessageText,
    clearMessageDraft,
    sortedMessages,
    displayMessages,
    focusedRootId,
    isMessagesInitialLoad,
    messagesError,
    refetchMessages,
    handleRunChatMessageQuery,
    chatMessageQueryLoadingId,
    handleSendMessage,
    isSendingMessage,
    expandThread,
    collapseThread,
    replaceStripFocusParam,
    messagesFeatureActive: queryEnabled,
  };
}
